import React, { useEffect, useState } from 'react';
import { Box, Typography, Card, CardContent, ToggleButton, ToggleButtonGroup, TextField, FormControlLabel, Checkbox } from '@mui/material';

import { reportingUsageAPI } from '../services/reportingUsageApi';

// Small pieces shared by the usage reporting pages (customers, customer overview, sessions, usage detail, bundles).
export const PERIODS = { last_month: 'Last month', last_year: 'Last 12 months', all_time: 'All time' };
export const PALETTE = ['#1976d2', '#2e7d32', '#ed6c02', '#7b1fa2', '#c2185b', '#00838f', '#6d4c41', '#558b2f', '#f9a825', '#5e35b1'];
export const num = (n) => Number(n || 0).toLocaleString();
export const errText = (err, fallback) => err?.response?.data?.detail || fallback;
export const limit = (n) => (n == null ? 'Unlimited' : Number(n).toLocaleString());
export const limitGb = (g) => (g == null ? 'Unlimited' : `${Number(g).toLocaleString()} GB`);
export const monthShort = (m) => new Date(`${String(m).slice(0, 10)}T00:00:00Z`).toLocaleDateString('en-GB', { month: 'short', year: '2-digit', timeZone: 'UTC' });
export const monthLong = (m) => new Date(`${String(m).slice(0, 10)}T00:00:00Z`).toLocaleDateString('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' });
export const TYPE_LABELS = { P: 'Portal', B: 'Background', L: 'Legacy', Unknown: 'Unknown' };
export const typeLabel = (t) => TYPE_LABELS[t] || t;

export function PeriodPicker({ value, onChange, label = 'Period' }) {
  return (
    <ToggleButtonGroup exclusive size="small" value={value} onChange={(e, v) => v && onChange(v)} aria-label={label}>
      {Object.entries(PERIODS).map(([k, text]) => <ToggleButton key={k} value={k}>{text}</ToggleButton>)}
    </ToggleButtonGroup>
  );
}

export function Stat({ label, value, sub }) {
  return (
    <Card>
      <CardContent sx={{ py: 1.25, px: 1.5, '&:last-child': { pb: 1.25 } }}>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="h5" component="div">{value}</Typography>
        {sub ? <Typography variant="caption" color="text.secondary">{sub}</Typography> : null}
      </CardContent>
    </Card>
  );
}

export const StatGrid = ({ children }) => <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 2, mb: 2 }}>{children}</Box>;

export function Legend({ items }) {
  return (
    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1 }}>
      {items.map((i) => (
        <Typography key={i.key} variant="caption"><Box component="span" sx={{ display: 'inline-block', width: 10, height: 10, bgcolor: i.color, mr: 0.5 }} />{i.label}</Typography>
      ))}
    </Box>
  );
}

const niceMax = (v) => {
  if (!(v > 0)) return 1;
  const p = 10 ** Math.floor(Math.log10(v));
  return ([1, 2, 2.5, 5, 10].map((s) => s * p).find((x) => x >= v)) || 10 * p;
};
const compact = (v) => (v >= 1e6 ? `${+(v / 1e6).toFixed(1)}M` : v >= 1e3 ? `${+(v / 1e3).toFixed(1)}k` : String(v));

// Bars drawn as SVG in the page's own theme colours. data: [{label, values: {key: number}}]; keys: [{key, label, color}].
// stacked: one bar per label with the keys on top of each other; otherwise the keys sit side by side.
export function BarChart({ data, keys, stacked = false, height = 220, ariaLabel, unit = '' }) {
  const W = 640, L = 44, T = 8, B = 24, H = height;
  const n = data.length;
  const total = (d) => keys.reduce((s, k) => s + Number(d.values[k.key] || 0), 0);
  const top = niceMax(Math.max(0, ...data.map((d) => (stacked ? total(d) : Math.max(0, ...keys.map((k) => Number(d.values[k.key] || 0)))))));
  const slot = (W - L - 6) / Math.max(n, 1);
  const bw = Math.max(2, Math.min(34, slot * 0.7)) / (stacked ? 1 : keys.length);
  const y = (v) => T + (H - T - B) * (1 - v / top);
  const every = Math.max(1, Math.ceil(n / 12));
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => f * top);
  return (
    <Box sx={{ width: '100%', overflow: 'hidden' }}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label={ariaLabel} style={{ display: 'block', maxHeight: H + 40 }}>
        {ticks.map((t) => (
          <g key={t}>
            <line x1={L} x2={W - 4} y1={y(t)} y2={y(t)} stroke="currentColor" opacity={0.12} />
            <text x={L - 6} y={y(t) + 3} fontSize="10" textAnchor="end" fill="currentColor" opacity={0.7}>{compact(t)}{unit}</text>
          </g>
        ))}
        {data.map((d, i) => {
          const cx = L + slot * i + slot / 2;
          let acc = 0;
          return (
            <g key={`${d.label}-${i}`}>
              <title>{`${d.label}: ${keys.map((k) => `${k.label} ${num(d.values[k.key])}${unit}`).join(', ')}`}</title>
              {keys.map((k, j) => {
                const v = Number(d.values[k.key] || 0);
                if (!v) return null;
                const x = stacked ? cx - bw / 2 : cx - (bw * keys.length) / 2 + j * bw;
                const yTop = y(stacked ? acc + v : v);
                const h = y(stacked ? acc : 0) - yTop;
                acc += v;
                return <rect key={k.key} x={x} y={yTop} width={Math.max(1, bw - 1)} height={Math.max(0.5, h)} fill={k.color} rx={1} />;
              })}
              {i % every === 0 && <text x={cx} y={H - 8} fontSize="10" textAnchor="middle" fill="currentColor" opacity={0.7}>{d.label}</text>}
            </g>
          );
        })}
      </svg>
    </Box>
  );
}

// Customers for a picker, newest data first load; `activeOnly` follows the checkbox.
export function useCustomers(activeOnly) {
  const [state, setState] = useState({ customers: [], error: '', loading: true });
  useEffect(() => {
    let live = true;
    setState((s) => ({ ...s, loading: true }));
    reportingUsageAPI.customers(activeOnly)
      .then((res) => live && setState({ customers: res.data || [], error: '', loading: false }))
      .catch((err) => live && setState({ customers: [], error: errText(err, 'Could not load customers'), loading: false }));
    return () => { live = false; };
  }, [activeOnly]);
  return state;
}

export function CustomerPicker({ customers, value, onChange, activeOnly, onActiveOnly, label = 'Customer' }) {
  const sorted = [...customers].sort((a, b) => String(a.cust_name).localeCompare(String(b.cust_name)));
  return (
    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
      <TextField select size="small" label={label} value={value || ''} onChange={(e) => onChange(e.target.value)} sx={{ minWidth: 280 }}
        slotProps={{ select: { native: true }, inputLabel: { shrink: true } }}>
        <option value="">Select a customer...</option>
        {sorted.map((c) => <option key={c.cust_code} value={c.cust_code}>{c.cust_name} ({c.cust_code})</option>)}
      </TextField>
      {onActiveOnly && <FormControlLabel control={<Checkbox size="small" checked={activeOnly} onChange={(e) => onActiveOnly(e.target.checked)} />} label="Active customers only" />}
    </Box>
  );
}
